import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

export type ScopeFilterSelectionMode = "single" | "multi";

/**
 * How this option is written to filter line items when applied with other options on the same `filterKey`:
 * - `equals` (default): one `equals` line per selected option.
 * - `in`: one `in` line for that key; `values` holds every selected value (including a single value when only one option is selected).
 */
export type ScopeFilterType = "equals" | "in";

export type ScopeFilterOptionType = "User" | "value";

/** Passed when applying / reading scope filters so `type: "User"` options use the signed-in workspace user id. */
export type ScopeFilterResolutionContext = {
    workspaceUserId: Uuid;
};

/**
 * One selectable scope row. Maps to filter line items (see `filterType` for `in` vs per-option `equals`).
 * - `User`: filter value is the current workspace user id when `ScopeFilterResolutionContext` is provided (use `value` for other ids, e.g. templateId).
 */
export type ScopeFilterOption = {
    id: Uuid;
    type: ScopeFilterOptionType;
    filterKey: string;
    defaultValue?: string;
    label: string;
    /** Defaults to `equals`. */
    filterType?: ScopeFilterType;
};

export type ScopeFilterConfig = {
    selectionMode?: ScopeFilterSelectionMode;
    /** Drawer / popover title (e.g. "Type"). */
    drawerTitle?: string;
    options: Array<ScopeFilterOption>;
};

export type OttopilotDataViewerConfig = {
    searchColumns: Array<string>;
    searchPlaceholder?: string;
    scopeFilter?: ScopeFilterConfig;
};

const scopeFilterOptionValidator: zod.ZodType<ScopeFilterOption> = zod.strictObject({
    id: uuidZodValidator,
    type: zod.enum(["User", "value"]),
    filterKey: zod.string(),
    defaultValue: zod.string().optional(),
    label: zod.string(),
    filterType: zod.enum(["equals", "in"]).optional(),
});

const scopeFilterConfigValidator: zod.ZodType<ScopeFilterConfig> = zod.strictObject({
    selectionMode: zod.enum(["single", "multi"]).optional(),
    drawerTitle: zod.string().optional(),
    options: scopeFilterOptionValidator.array(),
});

export const ottopilotDataViewerConfigValidator = zod.strictObject({
    searchColumns: zod.string().array(),
    searchPlaceholder: zod.string().optional(),
    scopeFilter: scopeFilterConfigValidator.optional(),
}) satisfies zod.ZodType<OttopilotDataViewerConfig>;
